/**
 * The Equipment table's place (the organization picked in the filter strip,
 * the search within it and the page) saved for the browser tab, so a refresh
 * on an entry page still comes back to the rows the user left. sessionStorage,
 * like the sign-in token: closing the tab forgets it.
 */

/** sessionStorage key of the saved Equipment table place. */
export const EQUIPMENT_LIST_STORAGE_KEY = 'rep-equipment-list';

export interface EquipmentListPlace {
  /** Organization picked in the filter strip; 0 = Show All. */
  organizationID: number;
  /** Table search; '' = none. */
  search: string;
  /** 1-based table page. */
  page: number;
}

const isWholeNumber = (value: unknown, min: number): value is number =>
  Number.isSafeInteger(value) && (value as number) >= min;

/** The parsed saved value as a place, or null when it is anything else (a hand edit, another shape). */
const toPlace = (value: unknown): EquipmentListPlace | null => {
  if (typeof value !== 'object' || value === null) return null;
  // A place saved before the table had a search has no `search`: it restores with none.
  const { organizationID, search = '', page } = value as Record<string, unknown>;
  return isWholeNumber(organizationID, 0) && typeof search === 'string' && isWholeNumber(page, 1)
    ? { organizationID, search, page }
    : null;
};

/** The place saved for this tab, or null to start on Show All, page 1. */
export const loadEquipmentListPlace = (): EquipmentListPlace | null => {
  // happy: a saved {organizationID, search, page} restores the table there.
  // nil: nothing saved in this tab yet (null) starts on page 1, no warning;
  //      a saved place without `search` restores with no search.
  // empty: '' or other text that is not JSON fails to parse below; a value of
  //        another shape warns after it. Both start on page 1.
  // upstream error: blocked storage throws; warn and start on page 1.
  let place: EquipmentListPlace | null;
  try {
    const saved = sessionStorage.getItem(EQUIPMENT_LIST_STORAGE_KEY);
    if (saved === null) return null;
    place = toPlace(JSON.parse(saved));
  } catch (error) {
    console.warn(
      `Equipment table: could not read sessionStorage "${EQUIPMENT_LIST_STORAGE_KEY}"; the table starts on page 1.`,
      error,
    );
    return null;
  }
  if (!place)
    console.warn(
      `Equipment table: sessionStorage "${EQUIPMENT_LIST_STORAGE_KEY}" holds no valid filter, search and page; the table starts on page 1.`,
    );
  return place;
};

/** Saves the place for this tab; when storage refuses, a refresh just starts on page 1. */
const saveEquipmentListPlace = ({ organizationID, search, page }: EquipmentListPlace) => {
  try {
    sessionStorage.setItem(
      EQUIPMENT_LIST_STORAGE_KEY,
      JSON.stringify({ organizationID, search, page }),
    );
  } catch (error) {
    console.warn(
      `Equipment table: could not save sessionStorage "${EQUIPMENT_LIST_STORAGE_KEY}"; a refresh starts the table on page 1.`,
      error,
    );
  }
};

interface EquipmentListStore {
  getState: () => { equipment: EquipmentListPlace };
  subscribe: (listener: () => void) => () => void;
}

/**
 * Saves the table place whenever the organization filter, the search or the
 * page changes, so the next load of this tab starts where the user left the table.
 */
export const keepEquipmentListPlace = (store: EquipmentListStore) => {
  let last = store.getState().equipment;
  return store.subscribe(() => {
    const { equipment } = store.getState();
    if (
      equipment.organizationID === last.organizationID &&
      equipment.search === last.search &&
      equipment.page === last.page
    )
      return;
    last = equipment;
    saveEquipmentListPlace(equipment);
  });
};
